import React, { useState, useEffect } from 'react';
import Textarea from '../../../components/ui/Textarea';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import FileUploadZone from './FileUploadZone';
import CoAuthorManager from './CoAuthorManager';
import { conferenceService } from '../../../services/conferenceService';
import { Checkbox } from '../../../components/ui/Checkbox';

const SubmissionForm = ({ conferenceId, onSubmit, isSaving, allowedFileTypes }) => {
  const { t } = useTranslation('submissions');
  const k = (key, opts) => t(`authorPortal.form.${key}`, opts);

  const [formData, setFormData] = useState({
    title: '',
    abstract: '',
    keywords: [],
    trackId: '',
    pdfFile: null,
    coAuthors: []
  });

  const [tracks, setTracks] = useState([]);
  const [conferenceKeywords, setConferenceKeywords] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(true);
  const [loadingConference, setLoadingConference] = useState(true);
  const [errors, setErrors] = useState({});

  // Fetch tracks and conference details
  useEffect(() => {
    const fetchConferenceData = async () => {
      try {
        setLoadingTracks(true);
        setLoadingConference(true);

        const [conferenceTracks, conferenceDetails] = await Promise.all([
          conferenceService?.getTracks(conferenceId),
          conferenceService?.getById(conferenceId)
        ]);

        setTracks(conferenceTracks || []);
        setConferenceKeywords(conferenceDetails?.keywords || []);
      } catch (err) {
        console.error('Failed to fetch conference data:', err);
      } finally {
        setLoadingTracks(false);
        setLoadingConference(false);
      }
    };

    if (conferenceId) {
      fetchConferenceData();
    }
  }, [conferenceId]);

  const trackOptions = tracks?.map(track => ({
    value: track?.id,
    label: track?.name,
    description: track?.description
  }));

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = k('validation.titleRequired');
    } else if (formData?.title?.length < 10) {
      newErrors.title = k('validation.titleTooShort');
    }

    if (!formData?.abstract?.trim()) {
      newErrors.abstract = k('validation.abstractRequired');
    } else if (formData?.abstract?.length < 100) {
      newErrors.abstract = k('validation.abstractTooShort');
    }

    if (!formData?.keywords?.length) {
      newErrors.keywords = k('validation.keywordsRequired');
    }

    if (!formData?.pdfFile) {
      newErrors.pdfFile = k('validation.fileRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleKeywordToggle = (keyword) => {
    const currentKeywords = formData?.keywords || [];
    if (currentKeywords?.includes(keyword)) {
      handleInputChange('keywords', currentKeywords?.filter(kw => kw !== keyword));
    } else {
      handleInputChange('keywords', [...currentKeywords, keyword]);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const wordCount = formData?.abstract?.split(' ')?.filter(w => w)?.length || 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-2">
        <div className="flex items-start gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-1">{k('title')}</h2>
            <p className="text-sm md:text-base text-muted-foreground">{k('subtitle')}</p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-6">
          <Input
            label={k('paperTitle.label')}
            type="text"
            placeholder={k('paperTitle.placeholder')}
            description={k('paperTitle.description')}
            value={formData?.title}
            onChange={(e) => handleInputChange('title', e?.target?.value)}
            error={errors?.title}
            required
          />

          {/* Was hand-rolled markup with no aria-describedby, so the word count
              and the validation error were on screen but never announced. */}
          <Textarea
            label={k('abstract.label')}
            rows={6}
            placeholder={k('abstract.placeholder')}
            value={formData?.abstract || ''}
            onChange={(e) => handleInputChange('abstract', e?.target?.value)}
            error={errors?.abstract}
            description={k('abstract.wordCount', { count: wordCount })}
            required
          />

          {conferenceKeywords?.length > 0 ? (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {k('keywords.label')} <span className="text-error">*</span>
              </label>
              <p className="text-xs md:text-sm text-muted-foreground mb-3">
                {k('keywords.selectHint')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {conferenceKeywords?.map((keyword, index) => (
                  <Checkbox
                    key={index}
                    label={keyword}
                    checked={formData?.keywords?.includes(keyword)}
                    onChange={() => handleKeywordToggle(keyword)}
                  />
                ))}
              </div>
              {errors?.keywords && (
                <p className="mt-2 text-xs md:text-sm text-error">{errors?.keywords}</p>
              )}
              {formData?.keywords?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {formData?.keywords?.map((keyword, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-md text-sm"
                    >
                      {keyword}
                      <button
                        type="button"
                        onClick={() => handleKeywordToggle(keyword)}
                        className="hover:text-error transition-colors duration-250"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Input
              label={k('keywords.label')}
              type="text"
              placeholder={k('keywords.placeholder')}
              description={k('keywords.description')}
              value={formData?.keywords?.join(', ')}
              onChange={(e) => handleInputChange('keywords', e?.target?.value?.split(',')?.map(kw => kw?.trim())?.filter(kw => kw))}
              error={errors?.keywords}
              required
              disabled={loadingConference}
            />
          )}

          <Select
            label={k('track.label')}
            options={trackOptions}
            value={formData?.trackId}
            onChange={(value) => handleInputChange('trackId', value)}
            error={errors?.trackId}
            placeholder={tracks?.length === 0 ? k('track.none') : k('track.placeholder')}
            description={tracks?.length === 0 ? k('track.none') : k('track.description')}
            disabled={loadingTracks || tracks?.length === 0}
            searchable
            clearable
          />
        </div>
      </div>
      <CoAuthorManager
        coAuthors={formData?.coAuthors}
        onCoAuthorsChange={(coAuthors) => handleInputChange('coAuthors', coAuthors)}
      />
      <FileUploadZone
        mainFile={formData?.pdfFile}
        onMainFileChange={(file) => handleInputChange('pdfFile', file)}
        supplementaryFiles={[]}
        onSupplementaryFilesChange={() => {}}
        error={errors?.pdfFile}
        allowedFileTypes={allowedFileTypes}
      />
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
        <Button
          type="submit"
          variant="default"
          size="lg"
          iconName="Send"
          iconPosition="right"
          className="flex-1"
          disabled={isSaving}
          loading={isSaving}
        >
          {k('submit')}
        </Button>
      </div>
    </form>
  );
};

export default SubmissionForm;
